import { memo } from "react";
import "@visioni/a3s-ui/theme";
import { StyleSheet } from "react-native-unistyles";
import { View, Text, Input, Button } from "@visioni/a3s-ui/components";
import { AccessCard } from "../AccessCard";
import { SiteAccessList, type SiteAccessListSite } from "../SiteAccessList";
import { CameraAccessList, type CameraAccessListCamera } from "../CameraAccessList";

export type InviteUserFormData = {
  email: string;
  tenantAdmin: boolean;
  sites: Map<string, "manager" | "viewer">;
  cameras: Map<string, "manager" | "viewer">;
};

export type InviteUserFormProps = {
  email: string;
  emailError?: string;
  permissionsError?: string;
  tenantAdmin: boolean;
  sites: SiteAccessListSite[];
  cameras: CameraAccessListCamera[];
  siteSelection: Map<string, "manager" | "viewer">;
  cameraSelection: Map<string, "manager" | "viewer">;
  /** Whether the form submission is in progress. Disables the submit button. */
  isPending?: boolean;
  onEmailChange: (text: string) => void;
  onTenantAdminToggle: () => void;
  onSiteToggle: (id: string) => void;
  onSiteRoleChange: (id: string, role: "manager" | "viewer") => void;
  onCameraToggle: (id: string) => void;
  onCameraRoleChange: (id: string, role: "manager" | "viewer") => void;
  onSubmit: () => void;
  onCancel: () => void;
};

const TENANT_ADMIN_ID = "tenant_admin";

export const InviteUserForm = memo(
  ({
    email,
    emailError,
    permissionsError,
    tenantAdmin,
    sites,
    cameras,
    siteSelection,
    cameraSelection,
    isPending = false,
    onEmailChange,
    onTenantAdminToggle,
    onSiteToggle,
    onSiteRoleChange,
    onCameraToggle,
    onCameraRoleChange,
    onSubmit,
    onCancel,
  }: InviteUserFormProps) => {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text textStyle="lg/semibold" style={styles.title}>
            Invite User
          </Text>
          <View style={styles.headerActions}>
            <Button label="Cancel" variant="secondary" onPress={onCancel} disabled={isPending} />
            <Button label="Submit" variant="primary" onPress={onSubmit} loading={isPending} />
          </View>
        </View>

        <Input
          value={email}
          onChangeText={onEmailChange}
          label="Email"
          placeholder="Enter email address"
          error={emailError}
          textInputProps={{ keyboardType: "email-address", autoCapitalize: "none", autoCorrect: false }}
        />

        <AccessCard
          id={TENANT_ADMIN_ID}
          name="Tenant Admin"
          subtitle="Full access to all sites and cameras"
          showThumbnail={false}
          showRoleControl={false}
          selected={tenantAdmin}
          role="manager"
          onToggle={onTenantAdminToggle}
          onRoleChange={onTenantAdminToggle}
        />

        <SiteAccessList
          sites={sites}
          selected={siteSelection}
          onToggle={onSiteToggle}
          onRoleChange={onSiteRoleChange}
        />

        <CameraAccessList
          cameras={cameras}
          selected={cameraSelection}
          onToggle={onCameraToggle}
          onRoleChange={onCameraRoleChange}
        />

        {permissionsError && (
          <Text textStyle="sm/regular" color="danger">
            {permissionsError}
          </Text>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.xl,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: {
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    gap: theme.spacing.md,
  },
}));
